import { createAsyncThunk } from '@reduxjs/toolkit';
import ApiService from '../../../infrastructure/services/api/services';
import { apiConfig } from '../../../application/configurations/apiConfig';
import { ApiConfig } from '../../../application/configurations/IConfig';
import { toggleLoading } from '../../components/pageLoader/loaderReducer';
import { appConstants } from '../../../application/configurations/constants';
import { UtilityService } from '../../../infrastructure/services/utils';
import { resultsConfigurations } from './config';
import { enableFlagForSuggestedOrRecentSearch } from './searchReducer';
import { store } from '../../../application/store';
import AnalyticsEvents from '../../../infrastructure/services/analytics/analyticsUseCase';
import { AdobeAnalyticsEvents } from '../../../infrastructure/services/analytics/adobeAnalyticsUseCase';
import { setBrowsePageSearchFlag } from './searchReducer';
import { PeopleSortConfig } from '../../components/sort/config';
import resultsSearchActions from '../results/searchUseCase';
import envConfig from '../../../application/configurations/envConfig';

/**
 * class for processing async functions based on actions
 */
class Search {
  private searchApiConfig: ApiConfig;

  private analyticsApiConfig: ApiConfig;
  
  private hybridSearchApiConfig: ApiConfig;


  private isSematicSearchEnabled: boolean = false;

  constructor() {
    this.searchApiConfig = { ...apiConfig.search };
    this.analyticsApiConfig = { ...apiConfig.analytics };
     this.hybridSearchApiConfig = { ...apiConfig.HybridSearch };
  }

//Call search and hybrid search API based on wordlength - Hybrid search

  private getSearchConfig(searchTerm: string, vertical: string) {
    console.log('Debugging getSearchConfig:', { searchTerm });
    // Count the number of keywords in the search term
    const wordCount = searchTerm.trim().split(/\s+/).length;
    // Use hybrid search for 4 or more keywords
    if(searchTerm.includes(":") && searchTerm.toLowerCase().includes("esc_")){
      this.isSematicSearchEnabled = false;

      return this.searchApiConfig;
    }
    else if (
      wordCount >= envConfig.HybridSearchWordLimit &&
      envConfig.HybridSearchAllowedVerticals.length <= 0
    ) {
      this.isSematicSearchEnabled = true;

      return this.hybridSearchApiConfig;
    } else if (
      wordCount >= envConfig.HybridSearchWordLimit &&
      envConfig.HybridSearchAllowedVerticals.includes(vertical)
    ) {
      this.isSematicSearchEnabled = true;

      return this.hybridSearchApiConfig;
    } 
     else if (
       vertical === appConstants.InsightsData || vertical === appConstants.verticals.People || vertical === appConstants.verticals.Data || vertical === appConstants.nonVerticals.PolicyFinder
     ) {
       return this.hybridSearchApiConfig;
     }
    else {
      this.isSematicSearchEnabled = false;

      return this.searchApiConfig;
    }
  }

  /**
   * async method with redux store middleware Thunk wrapper
   * this is used to access the action based api configuration and
   * add dynamic params then send to http request service.
   * this is also pass the response to
   * @param   {object} request the request object containing search query term & an optional start row.
   * @return  {object} return api response
   */
  doSearch = createAsyncThunk(
    'search',
    async (
      request: {
        searchTerm: string;
        vertical: string;
        filterByDate: number;
        accounts: Array<Object>;
        refinersClicked: any;
        flagForContentAge: boolean;
        flagForClear: boolean;
        startRow: number;
        browsePageSearch: boolean;
        sortByValue: {
          name: string;
          Value: string;
          Direction: number;
          SortType: number;
        };
        refinerValues: any;
      },
      thunkApi
    ) => {
      // dispatching action to show loader
      thunkApi.dispatch(toggleLoading(true));
      let responseData: any = [];
      let resultCount: number = 0;
      let peopleAccountId: Array<string | undefined> = [];
      let profilePhotoCollection: any = [];
      let refinedVerticals: any = [];
      let latency: number = 0;
      let nestedRefinersFinal: any = [];
      let verticalValue:string="";
      let BrowserData : any = store.getState().browseSearch.browseConfigJSON; 
      BrowserData.Verticals.forEach((vertical:any) => {
        if (request.vertical.toLowerCase() === vertical.Name) {
       verticalValue = vertical.filters.verticals;
        }
       });
      let vertical:string = verticalValue ? verticalValue : appConstants.verticals.Documents;

      const filterBrowseObject:any = BrowserData.Verticals.filter((configValue: any) => configValue.Name.toLowerCase() === request.vertical.toLowerCase());
      let resultsLimit: any = resultsConfigurations.resultsPerPage.filter(
        (option) => option.vertical === vertical
      );
      let noOfItemsPerPage = JSON.parse(
        localStorage.getItem(appConstants.noOfItemsPerPage) || '{}'
      );
      let itemsToSetPerPage: number = noOfItemsPerPage
        ? noOfItemsPerPage
        : appConstants.defaultItemsPerPage;
      let modelObj = UtilityService.getRequestModel(vertical);
      //const searchConfig = { ...this.searchApiConfig };
        const searchConfig = {
        ...this.getSearchConfig(request.searchTerm, request.vertical)
      };
      searchConfig.accountInfo = request.accounts;
      searchConfig.data = {
        ...searchConfig.data,
        // using browseConfiguredSearchTerm method to add the search term with browse config
        Query: request.searchTerm,
        verticals: vertical,
        Refiners: filterBrowseObject && filterBrowseObject.length > 0 ? Object.keys(filterBrowseObject[0].filters) : [appConstants.RefinerName],
        Size:
          request.startRow > resultsLimit[0].count - itemsToSetPerPage
            ? resultsLimit[0].count - request.startRow
            : itemsToSetPerPage,
        From: request.startRow ? request.startRow : 0,
        ReturnFields: modelObj ? Object.keys(modelObj) : [],
        Sort: []
      };

      // filter search by Date of content age API call condition
      if (request.filterByDate && modelObj.contentAge) {
        searchConfig.data.Filters = {
          Condition: 'AND',
          Rules: [
            {
              Field: modelObj.contentAge,
              Type: 'Date',
              Operator: 'Range',
              Value: `${UtilityService.getMonthDates(
                request.filterByDate
              )};${new Date().toISOString()}`
            },
            {
                Field: "ESC_EYCONTENTSTATUS",
                Type: "String",
                Operator: ":",
                Values: ["Publish"]
            }
          ]
        };
      }

      if (
        filterBrowseObject &&
        filterBrowseObject.length > 0 &&
        filterBrowseObject[0].QueryConditionFilters &&
        filterBrowseObject[0].QueryConditionFilters.length > 0
      ) {
        filterBrowseObject[0].QueryConditionFilters.forEach((item: any) => {
          searchConfig.data.Filters.Rules.push(item);
        });
      }

      // Refiners API call condition
      if(request.refinerValues !== undefined && request.refinerValues.length > 0) {
        request.refinerValues.forEach((item: any) => {
          UtilityService.selectedRefiners(item, searchConfig.data.Filters.Rules);
        });
      }
      //  sort by filter API call condition
      if (request.sortByValue && request.sortByValue !== undefined) {
        searchConfig.data.Sort.push({
          FieldName: request.sortByValue.Value,
          Ascending: request.sortByValue.Direction == 1 ? false : true
        });
        if (
          request.vertical === appConstants.verticals.People &&
          (request.sortByValue.Value ===
            PeopleSortConfig.filterOptions[1].Value ||
            request.sortByValue.Value ===
              PeopleSortConfig.filterOptions[3].Value)
        ) {
          searchConfig.data.Sort.push({
            FieldName:
              request.sortByValue.Value ===
              PeopleSortConfig.filterOptions[1].Value
                ? PeopleSortConfig.filterOptions[3].Value
                : PeopleSortConfig.filterOptions[1].Value,
            Ascending: request.sortByValue.Direction == 1 ? false : true
          });
        }
      }
      await ApiService.requestApi([searchConfig])
        .then(
          (response: { [key: string]: any }) => {
            // Variable for backend failure counts i.e., isError property count in the actual API response object
            let errorCount: number = 0;
            if (response && response[0] && response[0].result.relevantResults) {

              try {
                // Refiners
                // First Level
                let level1Transformation = UtilityService.convertRefinersLevel1(JSON.parse(response[0].result.refinementResultsJSON));

                // Second Level
                level1Transformation.map((refine: any) => {
                  let object1: any = {};
                  //Condition to check if the Refiner is from AEM using path keyword.
                  if (refine.title.indexOf('UFPATH') > -1) {
                    object1 = {
                      "title": refine.title,
                      "refId": refine.refId,
                      "ID": refine.ID,
                      "source": UtilityService.convertRefinersLevel2({
                        "RefinementResults": refine.source.slice(1)
                      })
                    }
                  } else {
                    object1 = {
                      "title": refine.title,
                      "refId": refine.refId,
                      "ID": refine.ID,
                      "source": UtilityService.convertRefinersLevel2({
                        "RefinementResults": refine.source
                      })
                    }
                  }

                  object1.title = filterBrowseObject[0] && filterBrowseObject[0].filters[object1.title] !== undefined ? filterBrowseObject[0].filters[object1.title] : object1.title; 

                  nestedRefinersFinal.push(object1);
                });

                // Dont remove this console until we implement Refiners. 
                console.log(nestedRefinersFinal);

              } catch (error) {
                console.log(error);
              }
              // Refiners logics ends here.

              response[0].result.relevantResults.forEach((record: any) => {
                let federatorData = JSON.parse(record.FederatorMetadata);
                if (federatorData && federatorData.length > 0) {
                  let backendName = federatorData.find((obj: any) => {
                    return obj.Key === 'FederatorBackend';
                  }).Value;
                  switch (backendName) {
                    case appConstants.backendDataSources.people:
                      record.vertical = appConstants.verticals.People;
                      peopleAccountId.push(record.accountId);
                      break;
                      case appConstants.backendDataSources.people_stg:
                        record.vertical = appConstants.verticals.People;
                        peopleAccountId.push(record.accountId);
                        break;
                        case appConstants.elasticIndexNames.discoverpeople:
                      record.vertical = appConstants.verticals.People;
                      peopleAccountId.push(record.ESC_AADOBJECTID);
                      break;
                    default:
                      break;
                  }
                  responseData.push(
                    UtilityService.getResponseModel(record.vertical, record)
                  );
                } else {
                  // Increasing the backend failure count when the FederatorMetadata array is empty in the results.
                  errorCount++;
                }
              });
              // Setting up the Results Found count by decreasing with the backend failure count(errorCount).
              // This will fix for the backend failure scenarios i.e., when the backend returns any error in the response.
              resultCount =
                responseData.length > 0
                  ? response[0].result.totalRelevantResults - errorCount
                  : responseData.length;
              refinedVerticals = response[0].result.refinementResults;
              latency = response[0].result.totalTime;
              if (peopleAccountId.length) {
                thunkApi.dispatch(
                  resultsSearchActions.getUserProfilePic({
                    peopleAccountIds: peopleAccountId,
                    accounts: request.accounts
                  })
                );
              }
            }
          },
          (error) => {
            throw new Error(error);
          }
        )
        .finally(() => {
          // Calling the Analytics Events
          this.callAnalytics(request, resultCount);

          // injecting page load adobe Analytics Events
          AdobeAnalyticsEvents.injectAdobePageLoadEvents(
            request.searchTerm,
            resultCount,
            store.getState().general.userInfo,
            request.vertical,
            false
          );

          // dispatching action to hide loader
          thunkApi.dispatch(toggleLoading(false));
          thunkApi.dispatch(setBrowsePageSearchFlag(true));
          thunkApi.dispatch(enableFlagForSuggestedOrRecentSearch(''));
        });

      return {
        results: responseData,
        resultCount: resultCount,
        profilePhotoCollection: profilePhotoCollection,
        refiners: refinedVerticals,
        latency: latency,
        nestedRefiners: nestedRefinersFinal
      };
    }
  );

  /**
   * Method to trigger analytics actions
   * this is used to access the action based api configuration and
   * add dynamic params then send to http request service.
   * @param  {object} request the request object containing search query term & an optional start row.
   * @param  {number} resultCount search result count
   */
  callAnalytics = (
    request: {
      searchTerm: string;
      vertical: string;
      filterByDate?: number;
      accounts: Array<Object>;
      startRow?: number | undefined;
      browsePageSearch: boolean;
      refinersClicked?:Array<Object>;
      flagForContentAge?: boolean;
      flagForClear?: boolean;
    },
    resultCount: number | undefined
  ) => {
    const flagForSuggestedOrRecentSearch =
      store.getState().browseSearch.flagForSuggestedOrRecentSearch;

    if (flagForSuggestedOrRecentSearch !== '') {
      const wasRecentSearch =
        flagForSuggestedOrRecentSearch ===
        resultsConfigurations.RecentSearchClick
          ? 'true'
          : 'false';
      // Calling the RecentSearch and Suggestion Click analytics usecase
      AnalyticsEvents.recentSearchAndSuggestionsClick(
        request.searchTerm,
        request.accounts,
        request.browsePageSearch,
        wasRecentSearch,
        resultCount
      );
    } else {
      // Calling the Search Action analytics usecase
      AnalyticsEvents.searchAction(
        request.searchTerm,
        request.accounts,
        request.browsePageSearch,
        request.vertical,
        resultCount
      );
    }
    if (request.vertical !== appConstants.verticals.People) {
      AnalyticsEvents.refinerAction(
        request.searchTerm,
        request.accounts,
        request.browsePageSearch,
        request.filterByDate,
        request.refinersClicked,
        request.flagForContentAge,
        request.flagForClear,
        resultCount
      );
    }
  };

  /**
   * async method to trigger suggestions search action
   * this is used to access the action based api configuration and
   * add dynamic params then send to http request service.
   * this is also pass the response to
   * @param   {string} searchTerm  search query term
   */
  queryRecommondations = createAsyncThunk(
    'queryRecommondations',
    async (request: {
      searchTerms: string;
      visibleSuggestions: number;
      vertical: string;
    }) => {
      const suggestionsConfig = { ...apiConfig.querySuggestions };
      suggestionsConfig.data.Query = request.searchTerms;
      suggestionsConfig.data.Vertical = request.vertical;
      let responseData: any = [];
      await ApiService.requestApi([suggestionsConfig])
        .then((suggestionsArray: any) => {
          if (suggestionsArray.length > 0) {
            suggestionsArray[0].map((item: any) => {
              responseData.push(item);
            });
          }

        })
        .catch((error) => {
          console.log('Error' + error);
        });

      return {
        suggestionList: responseData
      };
    }
  );
}

const SearchUseCase = new Search();
export default SearchUseCase;
